import React, { useState, useEffect } from 'react';
import { ResourceSummary } from '../../services/awsService';
import { hardwareConfigService } from '../../services/hardwareConfigService';
import HardwareConfigModal from './HardwareConfigModal';
import { HardwareConfig } from './OnPremiseMigrationEstimate';
import { 
  CogIcon, ServerIcon, GlobeAltIcon, BuildingOfficeIcon, CpuChipIcon,
  CheckCircleIcon, XCircleIcon, InformationCircleIcon, CurrencyDollarIcon,
  CloudArrowUpIcon, ShieldCheckIcon, ChartBarIcon, ExclamationTriangleIcon,
  ArrowTrendingDownIcon, ArrowTrendingUpIcon, LightBulbIcon, ClockIcon,
  BoltIcon, UserGroupIcon, WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

interface AWSRepatriationPlanProps {
  summary: ResourceSummary;
  totalVcpus: number;
  totalMemory: number;
  totalStorage: number;
  totalMonthlyCost: number;
  bandwidthTotalGB: number;
  bandwidthCost: number;
}


const AWSRepatriationPlan: React.FC<AWSRepatriationPlanProps> = ({
  summary,
  totalVcpus,
  totalMemory,
  totalStorage,
  totalMonthlyCost,
  bandwidthTotalGB,
  bandwidthCost
}) => {
  const [activeTab, setActiveTab] = useState('aws');
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [hardwareConfig, setHardwareConfig] = useState<HardwareConfig | null>(null);

  const tabs = [
    { id: 'aws', name: 'AWS', icon: GlobeAltIcon },
    { id: 'enterprise', name: 'Enterprise Cloud', icon: ServerIcon },
    { id: 'private', name: 'Private Cloud', icon: BuildingOfficeIcon },
    { id: 'colocation', name: 'Colocation', icon: CpuChipIcon }
  ];

  useEffect(() => {
    loadHardwareConfig();
    
    const handleConfigUpdate = () => {
      loadHardwareConfig();
    };
    
    window.addEventListener('hardwareConfigUpdated', handleConfigUpdate);
    return () => window.removeEventListener('hardwareConfigUpdated', handleConfigUpdate);
  }, []);

  const loadHardwareConfig = async () => {
    try {
      const config = await hardwareConfigService.getDefaultConfig();
      setHardwareConfig(config);
    } catch (error) {
      console.error('Failed to load hardware config:', error);
    }
  };

  // Calculation functions
  const calculatePhysicalServers = () => {
    if (!hardwareConfig) return { servers: 0, actualCpus: 0, actualMemory: 0, actualStorage: 0 };
    
    const vcpuToCpuRatio = hardwareConfig.vcpuToCpuRatio || 3;
    const physicalCpus = hardwareConfig.sockets * hardwareConfig.coresPerCpu;
    
    // Calculate required physical resources
    const requiredCpus = totalVcpus / vcpuToCpuRatio;
    const requiredMemory = totalMemory;
    const requiredStorage = totalStorage;
    
    // Calculate servers needed based on each resource constraint
    const serversByCpu = Math.ceil(requiredCpus / physicalCpus);
    const serversByMemory = Math.ceil(requiredMemory / hardwareConfig.memoryPerServer);
    const serversByStorage = Math.ceil(requiredStorage / (hardwareConfig.storagePerServer * 1024)); // Convert TB to GB
    
    const servers = Math.max(serversByCpu, serversByMemory, serversByStorage);
    
    return {
      servers,
      actualCpus: servers * physicalCpus,
      actualMemory: servers * hardwareConfig.memoryPerServer,
      actualStorage: servers * hardwareConfig.storagePerServer
    };
  };

  const calculateCosts = () => {
    if (!hardwareConfig) return { aws: 0, enterprise: 0, private: 0, colocation: 0 };
    
    const physicalServers = calculatePhysicalServers();
    
    // Hardware costs (one-time, amortized over 3 years)
    const serverCost = physicalServers.servers * 30000; // Estimated per server
    
    // SAN costs
    const sanCost = hardwareConfig.mustUsePrivateSan 
      ? hardwareConfig.privateSanCost 
      : totalStorage * hardwareConfig.sharedSanCostPerTB / 1024; // Convert GB to TB
    
    // Network costs
    const switchCount = Math.ceil(physicalServers.servers / 40); // 40 servers per switch
    const switchCost = switchCount * hardwareConfig.costPer48PortSwitch;
    
    // Firewall costs
    const firewallCost = hardwareConfig.redundantFirewalls 
      ? hardwareConfig.costPerFirewall * 2 
      : hardwareConfig.costPerFirewall;
    
    // Total hardware cost amortized over 36 months
    const totalHardwareCost = serverCost + sanCost + switchCost + firewallCost;
    const monthlyHardwareCost = totalHardwareCost / 36;
    
    // Operational costs (monthly)
    const powerCost = physicalServers.servers * 200; // $200/server/month for power
    const coolingCost = physicalServers.servers * 100; // $100/server/month for cooling
    const networkBandwidthCost = bandwidthCost * 0.5; // Colocation bandwidth is cheaper
    const maintenanceCost = monthlyHardwareCost * 0.15; // 15% of hardware cost
    
    const colocationMonthlyCost = monthlyHardwareCost + powerCost + coolingCost + networkBandwidthCost + maintenanceCost;
    
    // Apply multipliers for different cloud options
    return {
      aws: totalMonthlyCost + bandwidthCost,
      enterprise: colocationMonthlyCost * 1.8, // 80% markup for managed service
      private: colocationMonthlyCost * 1.5,    // 50% markup for private cloud
      colocation: colocationMonthlyCost * 1.2  // 20% markup for facility costs
    };
  };

  // Recommendation logic
  const getRecommendations = () => {
    const costs = calculateCosts();
    const physicalServers = calculatePhysicalServers();
    const totalMemoryGB = totalMemory;
    const totalStorageGB = totalStorage;
    const hasEKS = summary.eks.clusters > 0;
    const hasS3 = summary.s3.buckets > 0;
    const hasNoSQL = (summary.dynamodb?.tables || 0) > 0;

    const recommendations = {
      aws: {
        viable: true,
        advice: "Continue with AWS for maximum flexibility and managed services.",
        factors: [] as string[],
        pros: [
          { icon: CloudArrowUpIcon, text: "Fully managed services" },
          { icon: GlobeAltIcon, text: "Global scale & availability" },
          { icon: BoltIcon, text: "No infrastructure management" },
          { icon: ClockIcon, text: "Instant scalability" }
        ],
        cons: [
          { icon: CurrencyDollarIcon, text: `Current monthly cost: $${(Number(costs.aws) || 0).toFixed(2)}` },
          { icon: ArrowTrendingUpIcon, text: "Costs can escalate quickly" },
          { icon: ShieldCheckIcon, text: "Data residency constraints" }
        ]
      },
      enterprise: {
        viable: costs.aws > 1000,
        advice: "",
        factors: [] as string[],
        pros: [
          { icon: ServerIcon, text: "Managed infrastructure" },
          { icon: ArrowTrendingDownIcon, text: "Potential cost savings" },
          { icon: UserGroupIcon, text: "Professional support" },
          { icon: ShieldCheckIcon, text: "Better compliance control" }
        ],
        cons: [
          { icon: ExclamationTriangleIcon, text: "Limited service portfolio" },
          { icon: LightBulbIcon, text: "Less innovation pace" },
          { icon: WrenchScrewdriverIcon, text: "Some vendor lock-in" }
        ]
      },
      private: {
        viable: costs.aws > 1000,
        advice: "",
        factors: [] as string[],
        pros: [
          { icon: ShieldCheckIcon, text: "Full control & sovereignty" },
          { icon: ArrowTrendingDownIcon, text: "Significant cost savings" },
          { icon: LightBulbIcon, text: "Customization flexibility" },
          { icon: BuildingOfficeIcon, text: "On-premises security" }
        ],
        cons: [
          { icon: WrenchScrewdriverIcon, text: "High operational overhead" },
          { icon: UserGroupIcon, text: "Requires skilled team" },
          { icon: ClockIcon, text: "Slower to scale" }
        ]
      },
      colocation: {
        viable: costs.aws > 1000,
        advice: "",
        factors: [] as string[],
        pros: [
          { icon: CpuChipIcon, text: "Maximum hardware control" },
          { icon: ArrowTrendingDownIcon, text: "Lowest TCO potential" },
          { icon: ShieldCheckIcon, text: "No vendor lock-in" },
          { icon: ChartBarIcon, text: "Predictable costs" }
        ],
        cons: [
          { icon: ExclamationTriangleIcon, text: "Highest complexity" },
          { icon: UserGroupIcon, text: "Requires expert team" },
          { icon: ClockIcon, text: "Long lead times" },
          { icon: WrenchScrewdriverIcon, text: "24/7 operations burden" }
        ]
      }
    };

    // Financial viability check
    if (costs.aws <= 1000) {
      ['enterprise', 'private', 'colocation'].forEach(option => {
        recommendations[option as keyof typeof recommendations].viable = false;
        recommendations[option as keyof typeof recommendations].advice = "❌ Not financially viable. Monthly spend below $1,000 makes migration costs prohibitive.";
      });
    } else {
      // Enterprise Cloud recommendations
      if (totalVcpus > 120 || totalMemoryGB > 1024) {
        recommendations.enterprise.advice = "⚠️ Not recommended. High resource requirements suggest private cloud or colocation.";
        recommendations.enterprise.factors.push("High vCPU/memory requirements");
      } else {
        recommendations.enterprise.advice = "✅ Viable option. Good balance of management and cost savings.";
      }

      // Private Cloud recommendations
      if (totalStorageGB > 40960 || totalVcpus > 120) {
        recommendations.private.advice = "✅ Recommended. High resource requirements make private cloud cost-effective.";
        recommendations.private.factors.push("High storage/compute requirements");
      } else {
        recommendations.private.advice = "⚠️ Consider carefully. May be overkill for current resource needs.";
      }

      // Colocation recommendations
      recommendations.colocation.advice = "⚠️ Highest complexity. Only for organizations with strong IT operations.";
      if (physicalServers.servers > 10) {
        recommendations.colocation.factors.push("Large server count justifies colocation");
      }
    }

    // Special considerations
    if (hasEKS) {
      ['enterprise', 'private', 'colocation'].forEach(option => {
        recommendations[option as keyof typeof recommendations].factors.push("EKS migration requires Kubernetes expertise");
      });
    }

    if (hasS3) {
      ['enterprise', 'private', 'colocation'].forEach(option => {
        recommendations[option as keyof typeof recommendations].factors.push("S3 replacement: Use Summit's managed object storage");
      });
    }

    if (hasNoSQL) {
      ['enterprise', 'private', 'colocation'].forEach(option => {
        recommendations[option as keyof typeof recommendations].factors.push("NoSQL replacement: Consider MongoDB, Cassandra, or similar");
      });
    }

    return recommendations;
  };

  const getCostBreakdown = (option: string) => {
    const costs = calculateCosts();
    const physicalServers = calculatePhysicalServers();
    
    const breakdown = [
      { service: 'Compute (EC2)', current: summary.ec2.monthlyComputeCost || 0 },
      { service: 'Storage (EBS/S3)', current: (summary.ebs.monthlyCost || 0) + (summary.s3.monthlyCost || 0) },
      { service: 'Database (RDS/DynamoDB)', current: (summary.rds?.totalMonthlyCost || 0) + (summary.dynamodb?.monthlyCost || 0) },
      { service: 'Containers (ECS/EKS)', current: (summary.ecs?.monthlyCost || 0) + (summary.eks.monthlyComputeCost || 0) },
      { service: 'Other Services', current: (summary.cloudwatchLogs?.monthlyCost || 0) + (summary.cloudfront?.monthlyCost || 0) },
      { service: 'Bandwidth', current: bandwidthCost }
    ];

    // Calculate estimated costs for each service in the target option
    const optionCosts = costs[option as keyof typeof costs];
    const totalCurrentCost = breakdown.reduce((sum, item) => sum + item.current, 0);
    
    return breakdown.map(item => ({
      ...item,
      estimated: optionCosts * (item.current / totalCurrentCost)
    }));
  };

  const recommendations = getRecommendations();
  const costs = calculateCosts();
  const physicalServers = calculatePhysicalServers();

  const renderTabContent = () => {
    const recommendation = recommendations[activeTab as keyof typeof recommendations];
    const costBreakdown = getCostBreakdown(activeTab);
    const monthlyCost = costs[activeTab as keyof typeof costs];
    const savings = costs.aws - monthlyCost;
    const savingsPercent = costs.aws > 0 ? (savings / costs.aws) * 100 : 0;

    return (
      <div className="relative">
        {/* Prominent Cost Display in Upper Right */}
        <div className="absolute -top-16 right-0 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg p-4 shadow-lg">
          <div className="text-xs uppercase tracking-wide opacity-90 mb-1">Estimated Monthly Cost</div>
          <div className="text-2xl font-bold">${(Number(monthlyCost) || 0).toFixed(2)}</div>
          {activeTab !== 'aws' && savings > 0 && (
            <div className="text-sm mt-1 flex items-center">
              <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
              Save ${(Number(savings) || 0).toFixed(0)} ({(Number(savingsPercent) || 0).toFixed(0)}%)
            </div>
          )}
        </div>

        <div className="space-y-6 pt-2">
          {/* Recommendation Card */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-start space-x-3 mb-4">
              <div className={`p-2 rounded-lg ${
                recommendation.advice.includes('✅') ? 'bg-green-100 dark:bg-green-900' : 
                recommendation.advice.includes('⚠️') ? 'bg-amber-100 dark:bg-amber-900' :
                'bg-blue-100 dark:bg-blue-900'
              }`}>
                <InformationCircleIcon className={`h-5 w-5 ${
                  recommendation.advice.includes('✅') ? 'text-green-600 dark:text-green-400' : 
                  recommendation.advice.includes('⚠️') ? 'text-amber-600 dark:text-amber-400' :
                  'text-blue-600 dark:text-blue-400'
                }`} />
              </div>
              <div>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                  Recommendation
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {recommendation.advice}
                </p>
              </div>
            </div>
            
            {recommendation.factors.length > 0 && (
              <div className="mt-4 p-3 bg-white/50 dark:bg-gray-900/50 rounded-lg">
                <h5 className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center">
                  <LightBulbIcon className="h-4 w-4 mr-1" />
                  Key Considerations
                </h5>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  {recommendation.factors.map((factor, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-indigo-500 mr-2">•</span>
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Pros and Cons Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pros Card */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-5 border border-green-200 dark:border-green-800">
              <h5 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-3 flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Advantages
              </h5>
              <ul className="space-y-2">
                {recommendation.pros.map((pro, index) => {
                  const Icon = pro.icon;
                  return (
                    <li key={index} className="flex items-start text-sm">
                      <Icon className="h-4 w-4 text-green-600 dark:text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-green-800 dark:text-green-200">{pro.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Cons Card */}
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-5 border border-red-200 dark:border-red-800">
              <h5 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-3 flex items-center">
                <XCircleIcon className="h-5 w-5 mr-2" />
                Considerations
              </h5>
              <ul className="space-y-2">
                {recommendation.cons.map((con, index) => {
                  const Icon = con.icon;
                  return (
                    <li key={index} className="flex items-start text-sm">
                      <Icon className="h-4 w-4 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-red-800 dark:text-red-200">{con.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          
          {/* Physical Requirements (for non-AWS tabs) */}
          {activeTab !== 'aws' && hardwareConfig && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-800">
              <h5 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-3 flex items-center">
                <ServerIcon className="h-5 w-5 mr-2" />
                Hardware Requirements
              </h5>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Physical Servers</div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{physicalServers.servers}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">vCPU Ratio</div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{hardwareConfig.vcpuToCpuRatio || 3}:1</div>
                </div>
              </div>
            </div>
          )}

          {/* Cost Breakdown */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2" />
                Service Cost Breakdown
              </h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Service Category
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Current (AWS)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Estimated
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Savings
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {costBreakdown.map((item, index) => {
                    const difference = item.estimated - item.current;
                    const percentChange = item.current > 0 ? (difference / item.current) * 100 : 0;
                    
                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {item.service}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                          ${(Number(item.current) || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white text-right">
                          ${(Number(item.estimated) || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-sm text-right">
                          <div className={`inline-flex items-center ${
                            difference < 0 
                              ? 'text-green-600 dark:text-green-400' 
                              : difference > 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-500'
                          }`}>
                            {difference < 0 ? (
                              <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                            ) : difference > 0 ? (
                              <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                            ) : null}
                            <span className="font-medium">
                              ${Math.abs(Number(difference) || 0).toFixed(2)}
                            </span>
                            <span className="text-xs ml-1">
                              ({Math.abs(Number(percentChange) || 0).toFixed(0)}%)
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-50 dark:bg-gray-700/50 font-semibold">
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      Total Monthly Cost
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">
                      ${(Number(costs.aws) || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">
                      ${(Number(monthlyCost) || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <div className={`inline-flex items-center ${
                        savings > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-500'
                      }`}>
                        {savings > 0 && <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />}
                        <span>${Math.abs(Number(savings) || 0).toFixed(2)}</span>
                        <span className="text-xs ml-1">({(Number(savingsPercent) || 0).toFixed(0)}%)</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-xl p-5 border border-amber-200 dark:border-amber-800">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
                  Important Considerations
                </h4>
                <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-1">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Additional costs for disaster recovery and backup data centers not included
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Migration costs and downtime not factored into estimates
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Managed service replacements may require additional operational overhead
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Compliance and security requirements may impact implementation
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            AWS Repatriation Budget/Plan
          </h2>
          <button
            onClick={() => setIsConfigModalOpen(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            <CogIcon className="h-4 w-4 mr-2" />
            Configure
          </button>
        </div>
      </div>

      <div className="p-6 relative">
        {/* Tab Navigation with space for cost display */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6 pt-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? 'border-slate-900 text-slate-900 dark:border-slate-100 dark:text-slate-100'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }
                  `}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>

      {/* Hardware Config Modal */}
      {hardwareConfig && (
        <HardwareConfigModal
          isOpen={isConfigModalOpen}
          onClose={() => setIsConfigModalOpen(false)}
          config={hardwareConfig}
          onSave={async (config) => {
            try {
              await hardwareConfigService.updateDefaultConfig(config);
              setHardwareConfig(config);
              // Dispatch event to notify other components
              window.dispatchEvent(new Event('hardwareConfigUpdated'));
            } catch (error) {
              console.error('Failed to save hardware config:', error);
            }
          }}
        />
      )}
    </div>
  );
};

export default AWSRepatriationPlan;